import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, Dimensions, TouchableOpacity, Animated } from 'react-native';
import { Svg, Circle, Path, Text as SvgText, G } from 'react-native-svg';

const { width } = Dimensions.get('window');
const WHEEL_SIZE = width * 0.8;
const CENTER = WHEEL_SIZE / 2;
const RADIUS = WHEEL_SIZE / 2 - 20;

interface WheelOption {
  id: string;
  option_label: string;
  option_value: string;
  option_type: string;
  color?: string;
  probability_weight?: number;
}

interface SpinWheelProps {
  options: WheelOption[];
  onSpinComplete: (selectedOption: WheelOption) => void;
  disabled?: boolean;
  onBeforeSpin?: () => Promise<boolean> | boolean;
}

const SpinWheel: React.FC<SpinWheelProps> = ({
  options,
  onSpinComplete,
  disabled = false,
  onBeforeSpin,
}) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedOption, setSelectedOption] = useState<WheelOption | null>(null);
  const rotation = useRef(new Animated.Value(0)).current;
  const currentRotationRef = useRef(0);

  // Convert color name to hex or return valid color
  const normalizeColor = (color: string | undefined): string => {
    if (!color) return getColorForOption(0, 1);
    
    // If it's already a valid hex color, return it
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) return color;
    
    // Convert common color names to hex
    const colorMap: { [key: string]: string } = {
      'red': '#FF0000',
      'green': '#00FF00',
      'blue': '#0000FF',
      'yellow': '#FFFF00',
      'orange': '#FFA500',
      'purple': '#800080',
      'pink': '#FFC0CB',
      'cyan': '#00FFFF',
      'magenta': '#FF00FF',
      'white': '#FFFFFF',
      'black': '#000000',
      'gray': '#808080',
      'grey': '#808080',
    };
    
    const lowerColor = color.toLowerCase();
    if (colorMap[lowerColor]) {
      return colorMap[lowerColor];
    }
    
    // Fallback to generated color
    return getColorForOption(0, 1);
  };

  // Generate colors if not provided
  const getColorForOption = (index: number, total: number) => {
    const hue = (360 / total) * index;
    return `hsl(${hue}, 70%, 55%)`;
  };

  // Calculate angles for each option
  const calculateAngle = (index: number, total: number) => {
    const anglePerSlice = 360 / total;
    return anglePerSlice * index;
  };

  // Generate wheel segments
  const generateSegment = (index: number, total: number) => {
    const anglePerSlice = 360 / total;
    const startAngle = (anglePerSlice * index - 90) * (Math.PI / 180);
    const endAngle = (anglePerSlice * (index + 1) - 90) * (Math.PI / 180);

    const x1 = CENTER + RADIUS * Math.cos(startAngle);
    const y1 = CENTER + RADIUS * Math.sin(startAngle);
    const x2 = CENTER + RADIUS * Math.cos(endAngle);
    const y2 = CENTER + RADIUS * Math.sin(endAngle);

    const largeArc = anglePerSlice > 180 ? 1 : 0;

    const path = [
      `M ${CENTER} ${CENTER}`,
      `L ${x1} ${y1}`,
      `A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');

    // Calculate text position (middle of the arc)
    const midAngle = (startAngle + endAngle) / 2;
    const textRadius = RADIUS * 0.7;
    const textX = CENTER + textRadius * Math.cos(midAngle);
    const textY = CENTER + textRadius * Math.sin(midAngle);
    const textRotation = (midAngle * 180) / Math.PI;

    return { path, textX, textY, textRotation };
  };

  const handleSpin = async () => {
    if (isSpinning || disabled || options.length === 0) return;

    if (onBeforeSpin) {
      try {
        const allowed = await Promise.resolve(onBeforeSpin());
        if (!allowed) {
          return;
        }
      } catch (error) {
        return;
      }
    }

    setIsSpinning(true);
    
    // Calculate weighted probabilities
    const weights = options.map(opt => opt.probability_weight || 1);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    const random = Math.random() * totalWeight;
    
    let cumulativeWeight = 0;
    let selectedIndex = 0;
    
    for (let i = 0; i < weights.length; i++) {
      cumulativeWeight += weights[i];
      if (random <= cumulativeWeight) {
        selectedIndex = i;
        break;
      }
    }

    // Calculate the angle for the selected option
    const anglePerSlice = 360 / options.length;
    
    // The pointer is at the top (-90 degrees in our coordinate system)
    // Segments start drawing from -90 degrees
    // The absolute angle where segment i's middle would align with the pointer:
    const targetAbsoluteAngle = -(selectedIndex * anglePerSlice + anglePerSlice / 2);
    
    // Get current rotation value and normalize it to 0-360 range
    const currentRotation = currentRotationRef.current;
    const currentNormalized = ((currentRotation % 360) + 360) % 360; // Handle negative values
    const targetNormalized = ((targetAbsoluteAngle % 360) + 360) % 360;
    
    // Calculate the rotation needed to reach the target (always forward/clockwise)
    let rotationNeeded = targetNormalized - currentNormalized;
    if (rotationNeeded < 0) {
      rotationNeeded += 360; // Make sure we always rotate forward
    }
    
    // Add multiple full rotations for the spinning effect
    const spins = 5; // Number of full rotations
    const finalAngle = spins * 360 + rotationNeeded;
    
    // Calculate new total rotation
    const newRotation = currentRotation + finalAngle;
    currentRotationRef.current = newRotation;
    
    // Animate to final position
    Animated.timing(rotation, {
      toValue: newRotation,
      duration: 3000,
      useNativeDriver: true,
    }).start(() => {
      setIsSpinning(false);
      setSelectedOption(options[selectedIndex]);
      onSpinComplete(options[selectedIndex]);
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.wheelContainer}>
        <Animated.View
          style={[
            styles.wheel,
            {
              transform: [
                {
                  rotate: rotation.interpolate({
                    inputRange: [-10000, 10000],
                    outputRange: ['-10000deg', '10000deg'],
                  }),
                },
              ],
            },
          ]}>
          <Svg width={WHEEL_SIZE} height={WHEEL_SIZE}>
            {options.map((option, index) => {
              const color = normalizeColor(option.color) || getColorForOption(index, options.length);
              const segment = generateSegment(index, options.length);
              
              return (
                <G key={option.id}>
                  <Path
                    d={segment.path}
                    fill={color}
                    stroke={selectedOption?.id === option.id ? "#FFD700" : "#fff"}
                    strokeWidth={selectedOption?.id === option.id ? 4 : 2}
                  />
                  {/* Text positioning */}
                  <G transform={`translate(${segment.textX}, ${segment.textY}) rotate(${segment.textRotation})`}>
                    <SvgText
                      fontSize="12"
                      fontWeight="bold"
                      fill={selectedOption?.id === option.id ? "#FFD700" : "#fff"}
                      textAnchor="middle"
                      x={0}
                      y={4}
                    >
                      {option.option_label.length > 15 ? option.option_label.substring(0, 12) + '...' : option.option_label}
                    </SvgText>
                  </G>
                </G>
              );
            })}
            {/* Center circle */}
            <Circle
              cx={CENTER}
              cy={CENTER}
              r={60}
              fill="#fff"
              stroke="#000"
              strokeWidth={3}
            />
          </Svg>
        </Animated.View>
        
        {/* Pointer */}
        <View style={styles.pointer}>
          <View style={[
            styles.pointerTriangle,
            selectedOption && styles.pointerTriangleHighlighted
          ]} />
        </View>
      </View>

      {/* Spin Button - Moved above results */}
      <TouchableOpacity
        style={[
          styles.spinButton,
          (isSpinning || disabled) && styles.spinButtonDisabled
        ]}
        onPress={handleSpin}
        disabled={isSpinning || disabled}
      >
        <Text style={styles.spinButtonText}>
          {isSpinning ? 'Spinning...' : 'SPIN AGAIN'}
        </Text>
      </TouchableOpacity>

      {/* Selected Option Display */}
      {selectedOption && (
        <View style={styles.selectedOptionContainer}>
          <Text style={styles.selectedOptionLabel}>🎉 Lucky Result!</Text>
          <Text style={styles.selectedOptionText}>
            {selectedOption.option_label}
          </Text>
          {selectedOption.option_type === 'number' && (
            <Text style={styles.selectedOptionValue}>
              Value: {selectedOption.option_value}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  wheelContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wheel: {
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
  },
  pointer: {
    position: 'absolute',
    top: -10,
    alignItems: 'center',
    zIndex: 10,
  },
  pointerTriangle: {
    width: 0,
    height: 0,
    borderLeftWidth: 15,
    borderRightWidth: 15,
    borderBottomWidth: 30,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#ff4444',
  },
  pointerTriangleHighlighted: {
    borderBottomColor: '#FFD700',
    shadowColor: '#FFD700',
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.8,
    shadowRadius: 10,
    elevation: 10,
  },
  spinButton: {
    marginTop: 20,
    backgroundColor: '#007AFF',
    paddingHorizontal: 60,
    paddingVertical: 16,
    borderRadius: 30,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
  },
  spinButtonDisabled: {
    backgroundColor: '#ccc',
  },
  spinButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  selectedOptionContainer: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f0f0f0',
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FFD700',
  },
  selectedOptionLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFD700',
    marginBottom: 5,
  },
  selectedOptionText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  selectedOptionValue: {
    fontSize: 14,
    color: '#666',
  },
});

export default SpinWheel;


